// collectionOperationDashboard/utils/format.js
// 看板公共格式化：率、百分比、坐标轴刻度
// 集中在这里的原因：揽收率同时出现在 KPI 卡、城市明细表、趋势图、UF 图四处，
// 口径和保留位数必须一致，避免各组件各写一套 toFixed 导致同屏数字对不上。

/**
 * 由分子/分母算揽收率（百分数），分母为 0 或非法时返回 null。
 * 为什么不用后端 collectRate：后端已按 1 位小数四舍五入（16/59 返回 27.1），
 * 前端再 toFixed(2) 会显示成 27.10%，与真实值 27.12% 不符（2026-10-26 测试反馈）。
 */
export function rateOf(numerator, denominator) {
  const n = Number(numerator);
  const d = Number(denominator);
  if (!Number.isFinite(n) || !Number.isFinite(d) || d <= 0) return null;
  return (n / d) * 100;
}

/** 百分比展示；无有效值走 fallback，保证空态也是 0.00% 而不是 undefined */
export function formatPercent(value, digits = 2, fallback = '0.00%') {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return `${n.toFixed(digits)}%`;
}

/**
 * 数量轴刻度：绝对值小于 1000 时直接显示原值。
 * 为什么：原实现统一 `${Math.round(v / 1000)}k`，295 这种量级所有刻度都被抹成 "0k"。
 */
export function formatAxisCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  if (Math.abs(n) < 1000) return `${Math.round(n)}`;
  const k = n / 1000;
  return `${Number.isInteger(k) ? k : k.toFixed(1)}k`;
}

/**
 * 百分比轴上下界：先补边距再取整到 1 位。
 * 为什么：原实现直接 `minRate - 3` 交给 ECharts，4.67 - 3 = 1.6700000000000002
 * 会被原样渲染成刻度标签（2026-10-26 测试反馈「图2展示很奇怪」）。
 */
export function rateAxisBounds(valueList, padding = 3, floor = 0, ceil = 100) {
  const valid = (Array.isArray(valueList) ? valueList : [])
    .filter((v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v)))
    .map(Number);
  if (!valid.length) return { min: floor, max: ceil };

  // 上下界取整到整数：既避免 4.67-3=1.6700000000000002 直出，
  // 也避免出现 63.7% / 69.7% 这种非整刻度（ECharts 只按 min/max 均分，小数界会带出一串小数标签）
  const min = Math.max(floor, Math.floor(Math.min(...valid) - padding));
  let max = Math.min(ceil, Math.ceil(Math.max(...valid) + padding));
  // 所有值相同时上下界会重合，ECharts 会画出压扁的轴，补 1 个点避免
  if (max <= min) {
    max = Math.min(ceil, min + 1);
  }
  return { min: max > min ? min : Math.max(floor, max - 1), max };
}
